import { useEffect, useMemo, useRef, useState } from 'react';
import { collection, doc, onSnapshot, query } from 'firebase/firestore';
import { db } from '../../firebaseConfig';
import { useAuth } from '../../auth/AuthProvider';
import { normalizeOrder } from '../lib/normalizeOrder';
import { normalizeSettings } from '../lib/ontimeSettings';
import { normalizeOnTimeRole } from '../utils/access';

function sortOrders(rows) {
  return [...rows].sort((a, b) => (b.placedAt?.getTime?.() || 0) - (a.placedAt?.getTime?.() || 0));
}

const noop = () => undefined;

function cleanText(value = '') {
  return String(value || '').trim();
}

function profileDisplayName(profile = {}) {
  return cleanText(
    profile.displayName ||
    profile.name ||
    `${profile.fullName || profile.firstName || ''} ${profile.surname || profile.lastName || ''}`.trim() ||
    profile.email ||
    'Link driver'
  );
}

function normalizeAssignedStores(...values) {
  const out = [];
  const push = (value) => {
    if (!value) return;
    if (Array.isArray(value)) {
      value.forEach(push);
      return;
    }
    if (typeof value === 'object') {
      push(value.name || value.store || value.site || value.storeName || value.siteName || value.label || value.value);
      return;
    }
    String(value)
      .split(',')
      .map((item) => cleanText(item))
      .filter(Boolean)
      .forEach((item) => out.push(item));
  };
  values.forEach(push);
  return Array.from(new Set(out)).sort((a, b) => a.localeCompare(b));
}

function mapDriverProfile(snapshotId, data = {}) {
  const role = normalizeOnTimeRole(data.onTimeRole || data.role || '');
  const active = data.active !== false && data.archived !== true && data.removed !== true && data.disabled !== true;
  if (role !== 'driver' || !active) return null;

  const name = profileDisplayName(data);
  return {
    id: snapshotId,
    driverProfileId: snapshotId,
    profileId: snapshotId,
    uid: cleanText(data.uid || data.authUid),
    authUid: cleanText(data.authUid || data.uid),
    name,
    displayName: name,
    role,
    status: 'offline',
    online: false,
    active: true,
    assignedStores: normalizeAssignedStores(
      data.assignedStores,
      data.assignedSites,
      data.stores,
      data.sites,
      data.storeAccess,
      data.siteAccess,
      data.storeNames,
      data.siteNames,
      data.store,
      data.site,
      data.storeName,
      data.siteName,
      data.assignedStore,
      data.assignedSite
    ),
  };
}

function driverKey(value = '') {
  return cleanText(value).toLowerCase();
}

function usableDriverName(value = '') {
  const text = cleanText(value);
  if (!text) return '';
  if (/^link[_-]/i.test(text)) return '';
  if (/^driver[_-]/i.test(text)) return '';
  return text;
}


export function useOnTimeLiveData() {
  const { claims, user } = useAuth();
  const orgId = claims?.orgId || '';
  const uid = user?.uid || '';
  const [orderDocs, setOrderDocs] = useState([]);
  const [eventMap, setEventMap] = useState({});
  const [driverDocs, setDriverDocs] = useState([]);
  const [driverProfiles, setDriverProfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [settingsDoc, setSettingsDoc] = useState(null);
  const [usingMock, setUsingMock] = useState(false);
  const runRef = useRef(0);

  useEffect(() => {
    runRef.current += 1;
    const runId = runRef.current;
    let active = true;
    let ordersUnsub = noop;
    let driversUnsub = noop;
    let profilesUnsub = noop;
    let settingsUnsub = noop;
    let eventsUnsub = noop;

    if (!uid || !orgId) {
      setOrderDocs([]);
      setEventMap({});
      setDriverDocs([]);
      setDriverProfiles([]);
      setSettingsDoc(null);
      setUsingMock(false);
      setLoading(false);
      return () => {};
    }

    setLoading(true);

    try {
      ordersUnsub = onSnapshot(
        query(collection(db, 'orgs', orgId, 'ontimeOrders')),
        (snap) => {
          if (!active || runRef.current !== runId) return;
          try {
            setUsingMock(false);
            setOrderDocs(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
          } catch (error) {
            console.error('[OnTime] orders snapshot error', error);
            setUsingMock(false);
            setOrderDocs([]);
          } finally {
            setLoading(false);
          }
        },
        (error) => {
          console.error('[OnTime] orders listener failed', error);
          if (!active || runRef.current !== runId) return;
          setUsingMock(false);
          setOrderDocs([]);
          setLoading(false);
        }
      );
    } catch (error) {
      console.error('[OnTime] orders listener setup failed', error);
      setUsingMock(false);
      setOrderDocs([]);
      setLoading(false);
    }

    // Events are loaded by the dedicated order-events hook when an order is opened.
    // Keeping a global collectionGroup listener here caused intermittent Firestore
    // INTERNAL ASSERTION failures on the map/overview route during fast navigation.
    eventsUnsub = noop;
    setEventMap({});



    try {
      settingsUnsub = onSnapshot(
        doc(db, 'orgs', orgId, 'ontimeSettings', 'general'),
        (snap) => {
          if (!active || runRef.current !== runId) return;
          setSettingsDoc(snap.exists() ? snap.data() : {});
        },
        (error) => {
          console.error('[OnTime] settings listener failed', error);
          if (!active || runRef.current !== runId) return;
          setSettingsDoc({});
        }
      );
    } catch (error) {
      console.error('[OnTime] settings listener setup failed', error);
      setSettingsDoc({});
    }

    try {
      driversUnsub = onSnapshot(
        query(collection(db, 'orgs', orgId, 'ontimeDrivers')),
        (snap) => {
          if (!active || runRef.current !== runId) return;
          try {
            setDriverDocs(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
          } catch (error) {
            console.error('[OnTime] drivers snapshot error', error);
            setDriverDocs([]);
          }
        },
        (error) => {
          console.error('[OnTime] drivers listener failed', error);
          if (!active || runRef.current !== runId) return;
          setDriverDocs([]);
        }
      );
    } catch (error) {
      console.error('[OnTime] drivers listener setup failed', error);
      setDriverDocs([]);
    }

    try {
      profilesUnsub = onSnapshot(
        query(collection(db, 'orgs', orgId, 'linkProfiles')),
        (snap) => {
          if (!active || runRef.current !== runId) return;
          try {
            setDriverProfiles(snap.docs.map((d) => mapDriverProfile(d.id, d.data() || {})).filter(Boolean));
          } catch (error) {
            console.error('[OnTime] driver profile snapshot error', error);
            setDriverProfiles([]);
          }
        },
        (error) => {
          console.error('[OnTime] driver profile listener failed', error);
          if (!active || runRef.current !== runId) return;
          setDriverProfiles([]);
        }
      );
    } catch (error) {
      console.error('[OnTime] driver profile listener setup failed', error);
      setDriverProfiles([]);
    }

    return () => {
      active = false;
      try { ordersUnsub(); } catch (error) { console.error('[OnTime] orders unsubscribe failed', error); }
      try { eventsUnsub(); } catch (error) { console.error('[OnTime] events unsubscribe failed', error); }
      try { settingsUnsub(); } catch (error) { console.error('[OnTime] settings unsubscribe failed', error); }
      try { driversUnsub(); } catch (error) { console.error('[OnTime] drivers unsubscribe failed', error); }
      try { profilesUnsub(); } catch (error) { console.error('[OnTime] driver profiles unsubscribe failed', error); }
    };
  }, [orgId, uid]);

  const orders = useMemo(() => {
    const rows = (orderDocs || []).map((row) => normalizeOrder({ ...row, events: eventMap[row.id] || row.events || [] }));
    return sortOrders(rows);
  }, [eventMap, orderDocs]);

  const driverLoads = useMemo(() => {
    const map = {};
    orders.forEach((order) => {
      if (order.assignedDriver && ['ready_for_driver', 'out_for_delivery'].includes(order.stage)) {
        map[order.assignedDriver] = (map[order.assignedDriver] || 0) + 1;
      }
    });
    return map;
  }, [orders]);

  const driversWithLoad = useMemo(() => {
    const docByKey = new Map();
    (Array.isArray(driverDocs) ? driverDocs : []).forEach((driver) => {
      const keys = [
        driver.id,
        driver.name,
        driver.displayName,
        driver.driverName,
        driver.profileId,
        driver.driverProfileId,
        driver.uid,
        driver.authUid,
      ].map(driverKey).filter(Boolean);

      keys.forEach((key) => {
        if (!docByKey.has(key)) docByKey.set(key, driver);
      });
    });

    return (Array.isArray(driverProfiles) ? driverProfiles : []).map((profile) => {
      const match =
        docByKey.get(driverKey(profile.id)) ||
        docByKey.get(driverKey(profile.driverProfileId)) ||
        docByKey.get(driverKey(profile.name)) ||
        docByKey.get(driverKey(profile.displayName)) ||
        docByKey.get(driverKey(profile.uid)) ||
        docByKey.get(driverKey(profile.authUid)) ||
        {};

      const name = cleanText(usableDriverName(profile.name) || usableDriverName(profile.displayName) || usableDriverName(match.name) || usableDriverName(match.displayName) || profile.name || profile.displayName);
      const status = cleanText(match.status || match.state || (match.online ? 'online' : profile.status || 'offline')).toLowerCase() || 'offline';

      return {
        ...match,
        ...profile,
        id: profile.id,
        driverDocId: match.id,
        name,
        displayName: name,
        status,
        online: match.online === true || status === 'online' || status === 'available',
        assignedStores: profile.assignedStores,
        load: driverLoads[name] || driverLoads[profile.id] || driverLoads[match.id] || 0,
      };
    });
  }, [driverDocs, driverLoads, driverProfiles]);

  const settings = useMemo(() => normalizeSettings(settingsDoc || {}), [settingsDoc]);

  return { orgId, orders, drivers: driversWithLoad, settings, loading, usingMock };
}
